// 2_components/gift_coupon.js
// Gift coupons: map coupon code → sample product code. On apply, add that
// product then submit the coupon; UI marks the gift line (prefix, Zdarma,
// qty/remove lock). Natios CS/SK (+ clone) only.
//
// Add more pairs here:
//   "OTHERCODE": "NATXXXX",

import { getShopKey } from "../0_global/shop_map.js";

const GIFT_COUPONS = {
	A98R1X53LS3: "NAT0147MB",
};

const STORAGE_KEY = "natGiftCoupon";
const ROW_CLASS = "nat-gift-coupon-item";
const PREFIX_CLASS = "nat-gift-coupon-prefix";
const DETAIL_LOCK_CLASS = "nat-gift-coupon-detail-locked";
const DETAIL_NOTICE_CLASS = "nat-gift-coupon-detail-notice";

let applyInFlight = false;
let listenersBound = false;
let addToCartWrapped = false;

export function init() {
	if (!isGiftCouponShop()) return;

	bindListeners();
	wrapAddToCart();
	refresh();
}

function isGiftCouponShop() {
	const key = getShopKey();
	return key === "cs" || key === "sk" || key === "cs_clone";
}

function bindListeners() {
	if (listenersBound) return;
	listenersBound = true;

	document.addEventListener("submit", onCouponFormSubmit, true);
	document.addEventListener("click", onRemoveCouponClick, true);
	document.addEventListener("ShoptetCartUpdated", refresh);
	document.addEventListener("ShoptetDOMCartContentLoaded", refresh);
	document.addEventListener("ShoptetDOMContentLoaded", refresh);
}

function refresh() {
	if (applyInFlight) return;
	maybeRemoveOwnedGiftAfterCouponGone();
	rememberGiftFromCart();
	enforceGiftQuantityOne();
	syncUi();
}

function onCouponFormSubmit(event) {
	const form = event.target;
	if (!(form instanceof HTMLFormElement)) return;
	if (!isAddCouponForm(form)) return;

	const input = form.querySelector("#discountCouponCode, input[name='discountCouponCode']");
	if (!input) return;

	const couponCode = normalizeCouponCode(input.value);
	const productCode = getProductForCoupon(couponCode);
	if (!productCode) return;

	event.preventDefault();
	event.stopImmediatePropagation();
	if (applyInFlight) return;
	applyGiftCouponFlow(form, input, couponCode, productCode);
}

function onRemoveCouponClick(event) {
	const target = event.target;
	if (!(target instanceof Element)) return;
	if (!getGiftState()?.owned) return;

	const removeControl = target.closest(
		'[data-testid="buttonRemoveDiscountCoupon"], .applied-coupon button[type="submit"], .applied-coupon a, form[action*="removeDiscountCoupon"] button, form[action*="removeDiscountCoupon"] a',
	);
	if (!removeControl) return;

	setGiftState({ owned: "pending-remove" });
}

async function applyGiftCouponFlow(form, input, couponCode, productCode) {
	applyInFlight = true;

	try {
		if (!findGiftCartItem(productCode)) {
			const added = await addGiftProduct(productCode);
			if (!added) {
				submitCouponForm(form);
				return;
			}
		}
		setGiftState({ owned: true, couponCode, productCode });
		rememberGiftFromCart(productCode);

		await new Promise((resolve) => window.setTimeout(resolve, 100));

		const liveForm = document.querySelector(".discount-coupon form[action*='addDiscountCoupon']") || form;
		const liveInput = liveForm.querySelector("#discountCouponCode, input[name='discountCouponCode']") || input;
		if (liveInput) liveInput.value = couponCode;

		submitCouponForm(liveForm);
		await waitForCartSettle();
		rememberGiftFromCart(productCode);
	} finally {
		applyInFlight = false;
		refresh();
	}
}

function addGiftProduct(productCode) {
	return new Promise((resolve) => {
		if (!window.shoptet?.cartShared?.addToCart) {
			resolve(false);
			return;
		}

		let settled = false;
		const finish = (ok) => {
			if (settled) return;
			settled = true;
			document.removeEventListener("ShoptetCartUpdated", onUpdated);
			document.removeEventListener("ShoptetCartAddCartItemFailed", onFailed);
			resolve(ok);
		};

		const onUpdated = () => finish(Boolean(findGiftCartItem(productCode)));
		const onFailed = () => finish(false);

		document.addEventListener("ShoptetCartUpdated", onUpdated, { once: true });
		document.addEventListener("ShoptetCartAddCartItemFailed", onFailed, { once: true });
		window.setTimeout(() => finish(Boolean(findGiftCartItem(productCode))), 8000);

		window.shoptet.cartShared.addToCart({ productCode, amount: 1 }, true);
	});
}

function submitCouponForm(form) {
	if (!form) return;
	if (window.shoptet?.cart?.ajaxSubmitForm) {
		shoptet.cart.ajaxSubmitForm(form.getAttribute("action"), form, "functionsForCart", "cart", true);
		return;
	}
	form.submit();
}

function waitForCartSettle() {
	return new Promise((resolve) => {
		let done = false;
		const finish = () => {
			if (done) return;
			done = true;
			document.removeEventListener("ShoptetCartUpdated", onUpdated);
			document.removeEventListener("ShoptetDOMCartContentLoaded", onUpdated);
			resolve();
		};
		const onUpdated = () => window.setTimeout(finish, 50);
		document.addEventListener("ShoptetCartUpdated", onUpdated, { once: true });
		document.addEventListener("ShoptetDOMCartContentLoaded", onUpdated, { once: true });
		window.setTimeout(finish, 4000);
	});
}

function maybeRemoveOwnedGiftAfterCouponGone() {
	if (document.body.classList.contains("id--15")) return;
	const state = getGiftState();
	if (!state?.owned) return;
	if (isGiftCouponApplied()) return;

	const gift = findGiftCartItem(state.productCode);
	clearGiftState();
	if (!gift?.itemId || !window.shoptet?.cartShared?.removeFromCart) return;

	window.shoptet.cartShared.removeFromCart({ itemId: gift.itemId }, true);
}

function syncUi() {
	const giftRows = findGiftRows();
	const giftActive =
		(isGiftCouponApplied() && Boolean(findGiftCartItem())) ||
		(document.body.classList.contains("id--15") && giftRows.length > 0);

	document.querySelectorAll(`.${ROW_CLASS}`).forEach((row) => {
		if (giftActive) return;
		row.classList.remove(ROW_CLASS);
		row.querySelectorAll(`.${PREFIX_CLASS}`).forEach((el) => el.remove());
	});

	if (giftActive) {
		giftRows.forEach((row) => {
			row.classList.add(ROW_CLASS);
			prefixGiftName(row);
			applyFreePriceLabel(row);
		});
	}

	const applied = getAppliedGiftCoupon();
	const detailLocked = Boolean(applied && isGiftProductDetailFor(applied.productCode));
	document.body.classList.toggle(DETAIL_LOCK_CLASS, detailLocked);
	syncDetailLockNotice(detailLocked);
}

function syncDetailLockNotice(locked, attempt = 0) {
	document.querySelectorAll(`.${DETAIL_NOTICE_CLASS}`).forEach((el) => el.remove());
	if (!locked) return;

	const wrap = document.querySelector(".product-top .price-and-button-wrapper-wrapper");
	const inner = wrap?.querySelector(".price-and-button-wrapper");
	if (!wrap || !inner) {
		if (attempt < 40) {
			window.setTimeout(() => syncDetailLockNotice(true, attempt + 1), 50);
		}
		return;
	}

	const text = window.NAT.translations("giftCouponDetailLocked");
	if (!text) return;

	const notice = document.createElement("div");
	notice.className = DETAIL_NOTICE_CLASS;
	notice.textContent = text;
	inner.after(notice);
}

function enforceGiftQuantityOne() {
	if (applyInFlight || !isGiftCouponApplied()) return;

	const gift = findGiftCartItem();
	if (!gift?.itemId || Number(gift.quantity) <= 1) return;
	if (!window.shoptet?.cartShared?.updateQuantityInCart) return;

	window.shoptet.cartShared.updateQuantityInCart(
		{
			itemId: gift.itemId,
			priceId: gift.priceId,
			amount: 1,
		},
		true,
	);
}

function wrapAddToCart() {
	if (addToCartWrapped || !window.shoptet?.cartShared?.addToCart) return;

	addToCartWrapped = true;
	const original = window.shoptet.cartShared.addToCart.bind(window.shoptet.cartShared);
	window.shoptet.cartShared.addToCart = function (payload, silent) {
		const applied = getAppliedGiftCoupon();
		if (
			!applyInFlight &&
			applied &&
			normalizeProductCode(payload?.productCode) === applied.productCode
		) {
			return;
		}
		return original(payload, silent);
	};
}

function isGiftProductDetailFor(productCode) {
	if (!document.body.classList.contains("type-product")) return false;

	const product =
		(typeof window.getShoptetDataLayer === "function" && window.getShoptetDataLayer("product")) ||
		window.dataLayer?.[0]?.shoptet?.product;
	return normalizeProductCode(product?.code) === normalizeProductCode(productCode);
}

function prefixGiftName(row) {
	row.querySelectorAll(`.${PREFIX_CLASS}`).forEach((el) => el.remove());

	const prefixText = window.NAT.translations("giftCouponPrefix");
	if (!prefixText) return;

	row.querySelectorAll(
		".p-name > .main-link, .p-name > [data-testid='recapItemName'], .cart-item-name a, .cart-item-name [data-testid='recapItemName'], .cart-widget-product-name a",
	).forEach((target) => {
		const prefix = document.createElement("strong");
		prefix.className = PREFIX_CLASS;
		prefix.textContent = prefixText;
		target.prepend(prefix);
	});
}

function applyFreePriceLabel(row) {
	const freeText = window.NAT.translations("giftCouponFree");
	if (!freeText) return;

	row.querySelectorAll(
		'[data-testid="cartPrice"], .p-total .price-final, [data-testid="cartWidgetProductPrice"], [data-testid="recapItemPrice"], .cart-item-price, td.p-price, .p-quantity strong',
	).forEach((el) => {
		el.textContent = freeText;
	});
}

function findGiftRows() {
	return [
		...document.querySelectorAll(
			".cart-table tr.removeable, .cart-table tr[data-testid='recapItem'], .cart-item, .cart-widget-product",
		),
	].filter(isGiftRow);
}

function isGiftRow(row) {
	const state = getGiftState();
	const sku = normalizeProductCode(row.getAttribute("data-micro-sku"));
	if (sku && isConfiguredGiftProduct(sku)) return true;
	if (sku && state?.productCode && sku === state.productCode) return true;

	const name = normalizeName(
		row.querySelector("[data-testid='recapItemName'], .p-name, .cart-item-name, .cart-widget-product-name")?.textContent,
	);
	if (state?.name && name && namesMatch(name, state.name)) return true;

	if (isGiftCouponApplied() && isFullyDiscountedRow(row)) return true;

	return false;
}

function isFullyDiscountedRow(row) {
	const discountText =
		row.querySelector('[data-testid="cartItemDiscount"], .p-discount .show-tooltip, .p-discount .acronym')?.textContent ||
		"";
	if (/\b100\s*%/.test(discountText)) return true;

	const priceText =
		row.querySelector(
			'[data-testid="cartPrice"], [data-testid="recapItemPrice"], .p-total .price-final, .cart-item-price, td.p-price',
		)?.textContent || "";
	return isZeroPriceText(priceText);
}

function rememberGiftFromCart(preferredProductCode) {
	if (!getGiftState()?.owned && !isGiftCouponApplied()) return;

	const applied = getAppliedGiftCoupon();
	const productCode = normalizeProductCode(preferredProductCode) || applied?.productCode || getGiftState()?.productCode;
	const item = findGiftCartItem(productCode);
	if (!item) return;

	const code = normalizeProductCode(item.code) || productCode;
	const row = [...document.querySelectorAll(".cart-table tr.removeable, .cart-item, .cart-widget-product")].find(
		(el) => normalizeProductCode(el.getAttribute("data-micro-sku")) === code,
	);
	const name = normalizeName(
		row?.querySelector("[data-testid='recapItemName'], .p-name, .cart-item-name, .cart-widget-product-name, .main-link")
			?.textContent,
	);

	setGiftState({
		owned: getGiftState()?.owned || true,
		couponCode: applied?.couponCode || getGiftState()?.couponCode || null,
		productCode: code,
		itemId: item.itemId || getGiftState()?.itemId || null,
		name: name || getGiftState()?.name || null,
	});
}

function findGiftCartItem(preferredProductCode) {
	const cart = getCartItems();
	const state = getGiftState();
	const applied = getAppliedGiftCoupon();
	const targetCode =
		normalizeProductCode(preferredProductCode) ||
		applied?.productCode ||
		state?.productCode ||
		null;

	if (targetCode) {
		const match = cart.find((item) => normalizeProductCode(item.code) === targetCode);
		if (match) return match;
	}

	for (const productCode of getConfiguredProductCodes()) {
		const match = cart.find((item) => normalizeProductCode(item.code) === productCode);
		if (match) return match;
	}

	if (!isGiftCouponApplied()) return null;

	return (
		cart.find((item) => {
			const price = Number(item.priceWithVat ?? item.price);
			return Number.isFinite(price) && price === 0;
		}) || null
	);
}

function getProductForCoupon(couponCode) {
	const normalized = normalizeCouponCode(couponCode);
	if (!normalized) return null;

	for (const [coupon, product] of Object.entries(GIFT_COUPONS)) {
		if (normalizeCouponCode(coupon) === normalized) {
			return normalizeProductCode(product);
		}
	}
	return null;
}

function getConfiguredProductCodes() {
	return [...new Set(Object.values(GIFT_COUPONS).map((product) => normalizeProductCode(product)).filter(Boolean))];
}

function isConfiguredGiftProduct(productCode) {
	const normalized = normalizeProductCode(productCode);
	return Boolean(normalized && getConfiguredProductCodes().includes(normalized));
}

function getAppliedGiftCoupon() {
	const appliedCodes = getAppliedCouponCodes();
	for (const couponCode of appliedCodes) {
		const productCode = getProductForCoupon(couponCode);
		if (productCode) return { couponCode, productCode };
	}

	const state = getGiftState();
	if (state?.couponCode && state?.productCode && appliedCodes.includes(state.couponCode)) {
		return { couponCode: state.couponCode, productCode: state.productCode };
	}
	return null;
}

function isGiftCouponApplied() {
	return Boolean(getAppliedGiftCoupon());
}

function getAppliedCouponCodes() {
	const codes = [];

	const applied = document.querySelector(".discount-coupon .applied-coupon, .applied-coupon");
	if (applied) {
		const text = normalizeCouponCode(applied.textContent);
		for (const coupon of Object.keys(GIFT_COUPONS)) {
			const normalized = normalizeCouponCode(coupon);
			if (normalized && text.includes(normalized)) codes.push(normalized);
		}
	}

	const coupons = window.dataLayer?.[0]?.shoptet?.cartInfo?.discountCoupon;
	if (Array.isArray(coupons)) {
		coupons.forEach((coupon) => {
			const code = normalizeCouponCode(coupon?.code || coupon);
			if (code && getProductForCoupon(code)) codes.push(code);
		});
	} else if (coupons && typeof coupons === "object") {
		const code = normalizeCouponCode(coupons.code || "");
		if (code && getProductForCoupon(code)) codes.push(code);
	}

	return [...new Set(codes)];
}

function isAddCouponForm(form) {
	const action = form.getAttribute("action") || "";
	return action.includes("addDiscountCoupon") || Boolean(form.closest(".discount-coupon"));
}

function getCartItems() {
	try {
		if (typeof window.getShoptetDataLayer === "function") {
			return window.getShoptetDataLayer("cart") || [];
		}
	} catch (_) {
		/* ignore */
	}
	return window.dataLayer?.[0]?.shoptet?.cart || [];
}

function getGiftState() {
	try {
		return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
	} catch (_) {
		return null;
	}
}

function setGiftState(patch) {
	const next = { ...(getGiftState() || {}), ...patch };
	sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

function clearGiftState() {
	sessionStorage.removeItem(STORAGE_KEY);
}

function normalizeName(value) {
	return String(value || "")
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();
}

function namesMatch(a, b) {
	if (!a || !b) return false;
	return a === b || a.includes(b) || b.includes(a);
}

function isZeroPriceText(value) {
	const digits = String(value || "").replace(/[^\d]/g, "");
	return digits !== "" && /^0+$/.test(digits);
}

function normalizeCouponCode(value) {
	return String(value || "")
		.trim()
		.toUpperCase()
		.replace(/\s+/g, "");
}

function normalizeProductCode(value) {
	return String(value || "")
		.trim()
		.toUpperCase();
}
